'use client';

import { Button } from '@jouzen/ecom-components/v3/Button';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { cx } from 'class-variance-authority';
import { useTranslations } from 'next-intl';
import { useMemo, useState } from 'react';

import { trackAnalytics } from '@/analytics/track';
import { EventType } from '@/analytics/types';
import { useCobranded } from '@/app/contexts/CobrandedProvider';
import { box } from '@/app/styles/ui';
import type { LineItem, MessageKey, Order } from '@/types';
import { OrderStatus } from '@/types/Order';

import OrderInfo, { type OrderInfoProps } from '../OrderInfo';
import OrderItems from '../OrderItems';
import Svg from '../Svg';
import { SvgName } from '../Svg/types';

export interface OrderDisplayProps {
  readonly className?: string;
  readonly isEnrolled?: boolean;
  readonly lineItems: LineItem[];
  readonly ringCount: number;
  readonly orderId: Order['netsuiteOrderId'];
  readonly shippingAddress: OrderInfoProps['address'];
  readonly orderDate: Order['orderDate'];
  readonly onSelectOrder: (order: Order) => void;
  readonly status: Order['orderStatus'];
  readonly paymentMethod?: Order['paymentMethod'];
}

const OrderDisplay = ({
  className,
  isEnrolled,
  lineItems,
  ringCount,
  orderDate,
  orderId,
  shippingAddress,
  onSelectOrder,
  status,
  paymentMethod,
}: OrderDisplayProps) => {
  const t = useTranslations();
  const { config: cobrandedConfig } = useCobranded();

  // memberships and other add-ons are rows too, so go by rings rather than lineItems
  const isSingleRing = ringCount === 1;

  // We always want to show one item orders, so isSingleRing can be used as the default state.
  // isSingleRing being true will still hide the button as per the design.
  const [isLineItemsVisible, setIsLineItemsVisible] =
    useState<boolean>(isSingleRing);

  const toggleExpanded = () => {
    if (!isLineItemsVisible) {
      trackAnalytics(EventType.CTAClicked, {
        cta: 'View Order',
        action: 'view order',
        location: 'body',
        path: '/account',
      });
    }
    setIsLineItemsVisible((prev) => !prev);
  };

  const isComplete = status === OrderStatus.completed;

  const actionButtonText: { text: MessageKey; testid: string } = useMemo(() => {
    if (!isEnrolled) {
      return {
        text: 'eop_start_order_button',
        testid: 'order-display-start-order-button',
      };
    }
    if (isSingleRing) {
      return {
        text: 'eop_select_ring_button',
        testid: 'order-display-select-ring-button',
      };
    }
    return {
      text: 'eop_select_rings_button',
      testid: 'order-display-select-rings-button',
    };
  }, [isEnrolled, isSingleRing]);

  const statusText: MessageKey = useMemo(() => {
    switch (status) {
      case OrderStatus.completed:
        return 'eop_home_orders_page_status_completed';
      case OrderStatus.closed:
        return 'eop_home_orders_page_status_closed';
      case OrderStatus.expired:
      case OrderStatus.ineligible:
        return 'eop_home_orders_page_status_expired';
      default:
        return 'eop_home_orders_page_status_pending';
    }
  }, [status]);

  return (
    <div
      data-testid="order-display"
      className={cx(box({ p: 10 }), 'flex flex-col gap-10', className)}
    >
      <div className="flex items-center justify-between">
        <Typography
          data-testid="order-display-status"
          className="rounded bg-black px-3 py-1 uppercase tracking-wider"
          color="light"
          variant="eyebrow-small"
          weight="bold"
        >
          {t(statusText)}
        </Typography>

        {!isComplete ? (
          <Button
            onClick={onSelectOrder}
            data-testid={actionButtonText.testid}
            disabled={!orderId || status !== OrderStatus.pending}
          >
            {t(actionButtonText.text)}
          </Button>
        ) : null}

        {isComplete && !isSingleRing ? (
          <Button
            onClick={toggleExpanded}
            data-testid="order-display-view-order-button"
            variant="outlined-dark"
          >
            {isLineItemsVisible
              ? t('eop_hide_order_button')
              : t('eop_view_order_button')}
          </Button>
        ) : null}
      </div>

      <div className="flex w-full gap-10 rounded-lg bg-gray-100 p-6">
        {cobrandedConfig?.logo && (
          <div>
            <Svg
              className="h-[40px] w-[74px]"
              name={cobrandedConfig?.logo as SvgName}
              data-testid={`${cobrandedConfig?.displayName}-svg`}
            />
          </div>
        )}
        <div className="flex-1">
          <OrderInfo
            address={shippingAddress}
            orderStatus={status}
            paymentMethod={paymentMethod}
            details={{
              number: orderId!,
              date: new Date(orderDate!),
              quantity: ringCount,
            }}
          />
        </div>
      </div>

      {isLineItemsVisible && isComplete && Boolean(lineItems.length) && (
        <OrderItems lineItems={lineItems} />
      )}
    </div>
  );
};

export default OrderDisplay;
